<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { voiceCallsAPI } from 'dashboard/api/voice/inboxConfig';

// VoiceCallSummaryCard — see docs/design/ai_voice_platform.md §10.2.
//
// Renders a single voice call's recording + transcript + summary in one
// card. Mirrors the ASCII mock in §10.2 — header (📞 + duration +
// direction + agent) → audio player → sentiment/QA chips → summary
// paragraph → checkable action items → topic chips → action buttons.
//
// Action item checkboxes call back to the API immediately
// (optimistic UI is intentionally NOT used — the manager seeing the
// checkmark blink should mean the server confirmed the persistence).

const props = defineProps({
  callId: { type: [Number, String], required: true },
});

const { t } = useI18n();

const isLoading = ref(true);
const loadError = ref(null);
const data = ref(null);
const togglingIndex = ref(null);
const isReSummarizing = ref(false);
const isReTranscribing = ref(false);

// Default-collapsed so a long transcript doesn't push the rest of the
// page off-screen. The admin opens this when they want to verify the
// summary against the raw turns (§10.2 QA flow).
const isTranscriptExpanded = ref(false);
const showFullText = ref(false);

const call       = computed(() => data.value?.call);
const recording  = computed(() => data.value?.recording);
const transcript = computed(() => data.value?.transcript);
const summary    = computed(() => data.value?.summary);

// Render-ready segments. The backend stores either an array of
// { speaker, start_ms, end_ms, text } chunks (from Whisper or the
// seeder) or nothing at all when the pipeline hasn't run. We
// normalise here so the template doesn't need to know about the
// shape.
const transcriptSegments = computed(() => {
  const raw = transcript.value?.segments;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter(s => s && (s.text || s.full_text))
    .map(s => ({
      speaker: (s.speaker || 'unknown').toLowerCase(),
      text: s.text || s.full_text || '',
      startMs: Number(s.start_ms ?? s.start ?? 0),
      endMs: Number(s.end_ms ?? s.end ?? 0),
    }));
});

const hasTranscript = computed(
  () => transcriptSegments.value.length > 0 || !!transcript.value?.full_text
);

const formatTurnTime = ms => {
  const total = Math.max(0, Math.floor(Number(ms) / 1000));
  const m = Math.floor(total / 60);
  const s = String(total % 60).padStart(2, '0');
  return `${m}:${s}`;
};

const isCustomerSpeaker = speaker => {
  // Whisper/Twilio can label speakers in different ways depending on
  // the pipeline. Treat anything that isn't clearly the agent/AI as
  // the customer so the chat layout doesn't flip every other turn.
  const s = String(speaker || '').toLowerCase();
  return !['agent', 'ai', 'assistant', 'bot', 'human_agent'].includes(s);
};

const SENTIMENT_EMOJI = {
  positive: '🙂', neutral: '😐', negative: '🙁', mixed: '🤔'
};

const formatDuration = seconds => {
  const s = Number(seconds) || 0;
  const mins = Math.floor(s / 60);
  const secs = String(s % 60).padStart(2, '0');
  return `${mins}m ${secs}s`;
};

const sentimentLabel = computed(() => {
  if (!summary.value?.sentiment) return null;
  const emoji = SENTIMENT_EMOJI[summary.value.sentiment] || '😐';
  return `${emoji} ${summary.value.sentiment}`;
});

const fetchCall = async () => {
  if (!props.callId) return;
  isLoading.value = true;
  loadError.value = null;
  try {
    const { data: body } = await voiceCallsAPI.show(props.callId);
    data.value = body;
  } catch (err) {
    loadError.value =
      err?.response?.data?.error ||
      err?.message ||
      t('VOICE_CALL_CARD.ERRORS.LOAD_FAILED');
  } finally {
    isLoading.value = false;
  }
};

const reSummarize = async () => {
  if (!props.callId || isReSummarizing.value) return;
  isReSummarizing.value = true;
  try {
    await voiceCallsAPI.reSummarize(props.callId);
    useAlert(t('VOICE_CALL_CARD.RE_SUMMARIZE_QUEUED'));
    // The re-run is async (Sidekiq). Poll once after a short delay so
    // the manager sees the refreshed summary without manual refresh.
    setTimeout(fetchCall, 4000);
  } catch (err) {
    const message =
      err?.response?.data?.error ||
      err?.message ||
      t('VOICE_CALL_CARD.ERRORS.RE_SUMMARIZE_FAILED');
    useAlert(message);
  } finally {
    isReSummarizing.value = false;
  }
};

const reTranscribe = async () => {
  if (!props.callId || isReTranscribing.value) return;
  isReTranscribing.value = true;
  try {
    await voiceCallsAPI.reTranscribe(props.callId);
    useAlert(t('VOICE_CALL_CARD.RE_TRANSCRIBE_QUEUED'));
    // Whisper + PII redaction + summarize is a longer chain than
    // re-summarize alone — poll a bit later so the card has a better
    // chance of catching the refreshed pipeline state on the first try.
    setTimeout(fetchCall, 8000);
  } catch (err) {
    const message =
      err?.response?.data?.error ||
      err?.message ||
      t('VOICE_CALL_CARD.ERRORS.RE_TRANSCRIBE_FAILED');
    useAlert(message);
  } finally {
    isReTranscribing.value = false;
  }
};

const toggleActionItem = async (index, currentDoneAt) => {
  togglingIndex.value = index;
  try {
    const { data: body } = await voiceCallsAPI.toggleActionItem(
      props.callId, index, !currentDoneAt
    );
    data.value = { ...data.value, summary: body.summary };
  } catch (err) {
    const message =
      err?.response?.data?.error ||
      err?.message ||
      t('VOICE_CALL_CARD.ERRORS.ACTION_ITEM_FAILED');
    useAlert(message);
  } finally {
    togglingIndex.value = null;
  }
};

watch(() => props.callId, fetchCall, { immediate: true });
</script>

<template>
  <div class="rounded-lg border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
    <div
      v-if="isLoading"
      class="flex items-center justify-center gap-3 p-8 text-sm text-slate-500"
    >
      <span class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-emerald-500" />
      <span>{{ t('VOICE_CALL_CARD.LOADING') }}</span>
    </div>

    <div
      v-else-if="loadError"
      class="m-4 rounded-md border border-ruby-200 bg-ruby-50 p-4 text-sm text-ruby-700"
    >
      {{ loadError }}
    </div>

    <div v-else-if="call" class="flex flex-col gap-4 p-6">
      <!-- Header -->
      <header class="flex items-center justify-between gap-3 border-b border-slate-200 pb-3 dark:border-slate-700">
        <div class="flex items-center gap-2 text-sm font-medium text-slate-900 dark:text-slate-100">
          <span class="text-base">📞</span>
          <span>{{ t('VOICE_CALL_CARD.HEADER_VOICE_CALL') }}</span>
          <span class="text-slate-400">·</span>
          <span>{{ formatDuration(call.duration_seconds) }}</span>
          <span class="text-slate-400">·</span>
          <span class="capitalize">{{ call.direction }}</span>
          <span v-if="call.assigned_user" class="text-slate-400">·</span>
          <span v-if="call.assigned_user">{{ call.assigned_user.name }}</span>
        </div>
        <span
          class="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-400"
        >
          {{ call.status }}
        </span>
      </header>

      <!-- Recording row -->
      <div
        v-if="recording"
        class="flex items-center gap-3 rounded-md bg-slate-50 p-3 dark:bg-slate-800"
      >
        <audio
          v-if="recording.url"
          controls
          :src="recording.url"
          class="h-9 flex-1"
        />
        <span v-else class="text-xs italic text-slate-400">
          {{ t('VOICE_CALL_CARD.NO_RECORDING_URL') }}
        </span>
        <span
          v-if="sentimentLabel"
          class="rounded-md bg-white px-2 py-1 text-xs font-medium text-slate-700 dark:bg-slate-900 dark:text-slate-200"
        >
          {{ sentimentLabel }}
        </span>
        <span
          v-if="summary?.qa_score != null"
          class="rounded-md bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700"
        >
          QA: {{ summary.qa_score }}/100
        </span>
      </div>

      <!-- Summary text -->
      <section v-if="summary?.summary" class="flex flex-col gap-2">
        <h4 class="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {{ t('VOICE_CALL_CARD.SUMMARY_TITLE') }}
        </h4>
        <p class="text-sm leading-relaxed text-slate-800 dark:text-slate-200">
          {{ summary.summary }}
        </p>
      </section>

      <!-- Action items -->
      <section v-if="summary?.action_items?.length" class="flex flex-col gap-2">
        <h4 class="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {{ t('VOICE_CALL_CARD.ACTION_ITEMS_TITLE') }}
        </h4>
        <ul class="flex flex-col gap-1.5">
          <li
            v-for="(item, idx) in summary.action_items"
            :key="idx"
            class="flex items-start gap-2 text-sm"
          >
            <input
              type="checkbox"
              :checked="!!item.done_at"
              :disabled="togglingIndex === idx"
              class="mt-0.5 h-4 w-4 cursor-pointer accent-emerald-600 disabled:opacity-50"
              @change="toggleActionItem(idx, item.done_at)"
            />
            <span
              class="flex-1 text-slate-800 dark:text-slate-200"
              :class="{ 'line-through text-slate-400 dark:text-slate-500': item.done_at }"
            >
              <span v-if="item.owner" class="font-medium">{{ item.owner }}:</span>
              {{ item.text }}
              <span v-if="item.due" class="ml-1 text-xs text-slate-500">
                ({{ item.due }})
              </span>
            </span>
          </li>
        </ul>
      </section>

      <!-- Topics -->
      <section v-if="summary?.topics?.length" class="flex flex-wrap items-center gap-2">
        <h4 class="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {{ t('VOICE_CALL_CARD.TOPICS_TITLE') }}
        </h4>
        <span
          v-for="topic in summary.topics"
          :key="topic"
          class="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700"
        >
          {{ topic }}
        </span>
      </section>

      <!-- §10.2 Transcript history — collapsible chat-style replay so the
           manager can verify the AI summary against the actual turn
           history. Uses full_text_redacted via the API serializer, so PII
           stays out of the UI by default. -->
      <section v-if="hasTranscript" class="flex flex-col gap-2 border-t border-slate-200 pt-3 dark:border-slate-700">
        <button
          type="button"
          class="flex items-center justify-between gap-2 text-left"
          @click="isTranscriptExpanded = !isTranscriptExpanded"
        >
          <span class="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            {{ t('VOICE_CALL_CARD.TRANSCRIPT_HISTORY_TITLE') }}
            <span v-if="transcriptSegments.length" class="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              {{ t('VOICE_CALL_CARD.TRANSCRIPT_TURN_COUNT', { count: transcriptSegments.length }) }}
            </span>
          </span>
          <span class="text-xs text-slate-500">
            {{ isTranscriptExpanded ? t('VOICE_CALL_CARD.TRANSCRIPT_COLLAPSE') : t('VOICE_CALL_CARD.TRANSCRIPT_EXPAND') }}
            {{ isTranscriptExpanded ? '▴' : '▾' }}
          </span>
        </button>

        <div v-if="isTranscriptExpanded" class="flex flex-col gap-2">
          <!-- Chat-style bubble layout when we have proper segments -->
          <div v-if="transcriptSegments.length" class="flex flex-col gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800">
            <div
              v-for="(segment, idx) in transcriptSegments"
              :key="idx"
              class="flex"
              :class="isCustomerSpeaker(segment.speaker) ? 'justify-end' : 'justify-start'"
            >
              <div
                class="flex max-w-[80%] flex-col gap-0.5 rounded-lg px-3 py-1.5 text-sm"
                :class="
                  isCustomerSpeaker(segment.speaker)
                    ? 'bg-white text-slate-800 dark:bg-slate-700 dark:text-slate-100'
                    : 'bg-emerald-600 text-white'
                "
              >
                <div class="flex items-center gap-2 text-[10px] uppercase tracking-wide opacity-75">
                  <span>{{ isCustomerSpeaker(segment.speaker) ? '👤 ' + t('VOICE_CALL_CARD.SPEAKER_CUSTOMER') : '📞 ' + t('VOICE_CALL_CARD.SPEAKER_AGENT') }}</span>
                  <span>·</span>
                  <span>{{ formatTurnTime(segment.startMs) }}</span>
                </div>
                <div class="leading-snug">{{ segment.text }}</div>
              </div>
            </div>
          </div>

          <!-- Fallback: no segments but we have full_text. Show as a plain
               paragraph (less rich than the bubble view but still beats
               showing nothing) -->
          <div v-else-if="transcript?.full_text" class="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm leading-relaxed text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
            {{ transcript.full_text }}
          </div>

          <!-- Reveal full (un-redacted) text when the manager explicitly
               asks. Default state shows the redacted body. -->
          <button
            v-if="transcriptSegments.length && transcript?.full_text"
            type="button"
            class="self-start text-xs text-emerald-700 hover:underline"
            @click="showFullText = !showFullText"
          >
            {{ showFullText ? t('VOICE_CALL_CARD.TRANSCRIPT_HIDE_FULL') : t('VOICE_CALL_CARD.TRANSCRIPT_SHOW_FULL') }}
          </button>
          <div
            v-if="showFullText && transcript?.full_text"
            class="rounded-md border border-slate-200 bg-white p-3 text-xs leading-relaxed text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
          >
            {{ transcript.full_text }}
          </div>
        </div>
      </section>

      <!-- Manager actions (§10.2 footer) — re-summarize / re-transcribe
           both enqueue with force_new_ledger_entry so the cost ledger
           records the re-run as a separate line item per §14.5 v1.4.
           Visible whenever a completed recording exists so the manager
           can recover from a failed transcript too. -->
      <div
        v-if="recording"
        class="flex flex-wrap items-center gap-3 border-t border-slate-200 pt-3 dark:border-slate-700"
      >
        <button
          type="button"
          class="rounded-md border border-emerald-500 px-3 py-1.5 text-xs font-medium text-emerald-700 transition-colors hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-emerald-950"
          :disabled="isReSummarizing || !transcript?.full_text"
          @click="reSummarize"
        >
          <span v-if="isReSummarizing" class="inline-flex items-center gap-2">
            <span class="inline-block h-3 w-3 animate-spin rounded-full border-2 border-emerald-300 border-t-emerald-700" />
            {{ t('VOICE_CALL_CARD.RE_SUMMARIZING') }}
          </span>
          <span v-else>{{ t('VOICE_CALL_CARD.RE_SUMMARIZE') }}</span>
        </button>
        <button
          type="button"
          class="rounded-md border border-blue-500 px-3 py-1.5 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-blue-900"
          :disabled="isReTranscribing"
          @click="reTranscribe"
        >
          <span v-if="isReTranscribing" class="inline-flex items-center gap-2">
            <span class="inline-block h-3 w-3 animate-spin rounded-full border-2 border-blue-300 border-t-blue-700" />
            {{ t('VOICE_CALL_CARD.RE_TRANSCRIBING') }}
          </span>
          <span v-else>{{ t('VOICE_CALL_CARD.RE_TRANSCRIBE') }}</span>
        </button>
        <span class="text-xs text-slate-500">
          {{ summary ? t('VOICE_CALL_CARD.RE_SUMMARIZE_HINT') : t('VOICE_CALL_CARD.RE_TRANSCRIBE_HINT') }}
        </span>
      </div>

      <!-- Transcript status (when summary not ready yet) -->
      <div
        v-if="transcript && !summary"
        class="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400"
      >
        <p class="font-medium text-slate-700 dark:text-slate-300">
          {{ t(`VOICE_CALL_CARD.TRANSCRIPT_STATUS.${transcript.status.toUpperCase()}`) }}
        </p>
        <p v-if="transcript.last_error" class="mt-1 text-ruby-600">
          {{ transcript.last_error }}
        </p>
      </div>

      <!-- Empty pipeline state -->
      <div
        v-if="!transcript && !summary"
        class="rounded-md border border-dashed border-slate-300 p-4 text-center text-xs text-slate-500"
      >
        {{ t('VOICE_CALL_CARD.NO_PIPELINE_DATA') }}
      </div>
    </div>
  </div>
</template>
